import * as React from 'react'
// constants
import { ENTITIES } from 'constants/index'
// context
import { ObjectivesListAPIContext } from 'containers/ObjectivesList/layers/API'
import { ObjectivesListLinkingBLContext } from 'containers/ObjectivesList/layers/BL/Linking'
import { ObjectivesListNavigationBLContext } from 'containers/ObjectivesList/layers/BL/Navigation'
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// hooks
import { usePermissions } from 'hooks/usePermissions'
// types
import { IPlan, IPlanItem } from 'types/graphql'
// utils
import { handleError } from 'utils/errors/handlers'
import { applyTreeDeepLevels } from 'utils/objective'
import { getEntityTypeDescription } from 'utils/index'
import { addNodeUnderParent, getFlatDataFromTree, getTreeFromFlatData, removeNodeAtPath } from 'react-sortable-tree'
import { formatTree, generatePath, getKey, getNodeKey, getParentKey, increaseCreateFormPosition } from 'utils/tree'
// lodash
import { debounce, first, flow, get, isEmpty, omit, upperFirst } from 'lodash'
import { filter, getOr, last, map, maxBy, orderBy, pipe } from 'lodash/fp'

type TDebouncedSynchronizeTree = (tree: any, filterFn?: (node: any) => boolean) => void

interface IContext {
  debouncedSynchronizeTree: TDebouncedSynchronizeTree
  entities: any
  focuserState: boolean
  generateCreateRootEntityNode: (position: number, rootType: string) => any
  generateNewNode: (treeItem: any) => any
  getActiveCreateFormNode: () => { node: any; parentId: string } | null
  getTableCreateFormNode: () => any
  openTableCreateForm: (type: string, parent: any, typeOption?: any) => void
  submitRootCreate: (values: { name: string; rootType: any }) => Promise<any>
  tableCreateForm: any
  handleSetFocuserState: (value: boolean) => void
  plan: IPlan
  scrollToNode: (node: any) => any
  searchIndex: number
  setDeepLevel: (array: any, deepLevel?: number) => void
  setSearchIndex: (value: any) => void
}

interface IIncomingProps {
  plan: IPlan
  children?: React.ReactNode
}

export const ObjectivesListTreeBLContext = React.createContext<IContext>({} as any)

const ObjectivesListTreeBL: React.FC<IIncomingProps> = ({ children, plan }) => {
  const [searchIndex, setSearchIndex] = React.useState<number>(0)
  const [focuserState, setFocuserState] = React.useState<boolean>(true)

  const { isLinkingMode } = React.useContext(LinkingModeContext)
  const { showArchivedPlanItems } = React.useContext(PlanEditPageContext)

  const { createPlanItem, planItems, updatePlanTree } = React.useContext(ObjectivesListAPIContext)

  const { handleLinkObjective, handleLinkAction, handleLinkKPI, handleUnlinkPlanItem } =
    React.useContext(ObjectivesListLinkingBLContext)

  const { handleActionClick, handleKPIClick, handleObjectiveClick } = React.useContext(
    ObjectivesListNavigationBLContext
  )

  const activeCreateFormRef = React.useRef(null)

  // Active inline create-form for the TableTree (single at a time). State so opening/closing re-renders.
  const [tableCreateForm, setTableCreateForm] = React.useState<any>(null)

  // Bumped on every (re)open so each create-form gets a unique id: the row remounts with a fresh
  // (empty, autofocused) redux-form instead of keeping the previously submitted value.
  const tableCreateFormCounterRef = React.useRef(0)

  const { setParentPermissions } = usePermissions({
    entity: plan,
    isPlan: true
  })

  // constants
  const levels = flow((item) => get(item, 'planType.levels', []), orderBy('position', 'asc'))(plan)

  const planTypeLevelIds = map('id', levels)

  const entities = React.useMemo(
    () => [
      ...pipe(
        filter(({ planObjective }) => !!planObjective && (!showArchivedPlanItems ? !planObjective.archived : true)),
        map((item: IPlanItem) => {
          const isParentExist = planItems.some((planItem) => planItem.id === item.parentId)

          return {
            ...omit(item, ['planAction', 'planKPI', 'planObjective']),
            archived: item.planObjective.archived,
            entity: item.planObjective,
            entityId: item.planObjective.id,
            entityType: ENTITIES.PLAN_OBJECTIVE,
            ownerId: item.planObjective.ownedById,
            parentId: isParentExist ? item.parentId : null,
            referencedParentId: isParentExist ? null : item.parentId,
            path: generatePath(item.planObjective.planItems.data.find(({ referenced }) => !referenced)),
            levels
          }
        })
      )(planItems),
      ...pipe(
        filter(({ planAction }) => !!planAction && (!showArchivedPlanItems ? !planAction.archived : true)),
        map((item: IPlanItem) => {
          const isParentExist = planItems.some((planItem) => planItem.id === item.parentId)

          return {
            ...omit(item, ['planAction', 'planKPI', 'planObjective']),
            archived: item.planAction.archived,
            entity: item.planAction,
            entityId: item.planAction.id,
            entityType: ENTITIES.PLAN_ACTION,
            ownerId: item.planAction.ownedById,
            parentId: isParentExist ? item.parentId : null,
            referencedParentId: isParentExist ? null : item.parentId,
            path: generatePath(item.planAction.planItems.data.find(({ referenced }) => !referenced)),
            levels
          }
        })
      )(planItems),
      ...pipe(
        filter(({ planKPI }) => !!planKPI && (!showArchivedPlanItems ? !planKPI.archived : true)),
        map((item: IPlanItem) => {
          const isParentExist = planItems.some((planItem) => planItem.id === item.parentId)

          return {
            ...omit(item, ['planAction', 'planKPI', 'planObjective']),
            archived: item.planKPI.archived,
            entity: item.planKPI,
            entityId: item.planKPI.id,
            entityType: ENTITIES.PLAN_KPI,
            ownerId: item.planKPI.ownedById,
            parentId: isParentExist ? item.parentId : null,
            referencedParentId: isParentExist ? null : item.parentId,
            path: generatePath(item.planKPI.planItems.data.find(({ referenced }) => !referenced)),
            levels
          }
        })
      )(planItems)
    ],
    [plan.planType, plan.locked, planItems, isLinkingMode, showArchivedPlanItems]
  )

  const rootLevel = first(levels)

  if (!rootLevel) {
    return null
  }

  const lastPosition = flow(maxBy('treePosition'), getOr(0, 'treePosition'))(entities)

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const lastPositionRef = React.useRef(lastPosition)

  // eslint-disable-next-line react-hooks/rules-of-hooks
  React.useEffect(() => {
    lastPositionRef.current = lastPosition
  }, [lastPosition])

  // methods
  const handleSetFocuserState = (value: boolean) => setFocuserState(value)

  const getUpdatedLastPosition = () => {
    lastPositionRef.current = lastPositionRef.current + 1

    return lastPositionRef.current
  }

  const handleCloseCreateForm =
    ({ tree, path, setTree }) =>
    () => {
      activeCreateFormRef.current = null
      setTree(removeNodeAtPath({ treeData: tree, path, getNodeKey: getNodeKey as any }))
    }

  const generateNewNode = (treeItem) => {
    const additional: any = {}

    const linkingItems = {
      sourceLinkingItems: treeItem.entity.sourceLinkingItems || [],
      destinationLinkingItems: treeItem.entity.destinationLinkingItems || []
    }

    if (treeItem.entityType === ENTITIES.PLAN_OBJECTIVE) {
      additional.handleClick = (modalData) => handleObjectiveClick({ id: treeItem.entityId, ...modalData })
      additional.linkItem = handleLinkObjective(treeItem)
    }

    if (treeItem.entityType === ENTITIES.PLAN_ACTION) {
      additional.handleClick = (modalData) => handleActionClick({ id: treeItem.entityId, ...modalData })
      additional.linkItem = handleLinkAction(treeItem)
    }

    if (treeItem.entityType === ENTITIES.PLAN_KPI) {
      additional.handleClick = (modalData) => handleKPIClick({ id: treeItem.entityId, ...modalData })
      additional.linkItem = handleLinkKPI(treeItem)
    }

    additional.addCreateActionForm = addCreateActionForm(treeItem.nextLevel, treeItem)
    additional.addCreateKPIForm = addCreateKPIForm(treeItem.nextLevel, treeItem)
    additional.addCreateObjectiveForm = addCreateObjectiveForm(treeItem.nextLevel, treeItem)
    additional.unlinkItem = handleUnlinkPlanItem(linkingItems)

    return {
      ...additional,
      ...treeItem,
      plan,
      rootLevel,
      linkingItems,
      title: treeItem.entity.description,
      position: treeItem.treePosition
    }
  }

  const generateCreateRootEntityNode = (position = lastPosition, rootType) => ({
    id: new Date().getTime(),
    parentId: null,
    isCreateRoot: true,
    noChildren: true,
    firstLevel: rootLevel,
    position: position + 1,
    formName: `CREATE_ROOT_ENTITY_${new Date().getTime()}`,
    handleRootCreateSubmit: handleRootEntityCreateSubmit(rootLevel),
    handleTableRootCreateSubmit: handleTableRootCreateSubmit(),
    rootType,
    plan
  })

  const generateCreateObjectiveFormNode = (level, parent) => {
    return {
      id: new Date().getTime(),
      newEntityLevel: level,
      rootLevel,
      isCreateObjective: true,
      handleCloseCreateForm,
      handleObjectiveCreateSubmit: handleObjectiveCreateSubmit(level, parent)
    }
  }

  const generateCreateActionFormNode = (level, parent) => {
    return {
      id: new Date().getTime(),
      newEntityLevel: level,
      rootLevel,
      isCreateAction: true,
      handleCloseCreateForm,
      handleActionCreateSubmit: handleActionCreateSubmit(level, parent)
    }
  }

  const generateCreateKPIFormNode = (level, parent) => {
    return {
      id: new Date().getTime(),
      newEntityLevel: level,
      rootLevel,
      isCreateKPI: true,
      handleCloseCreateForm,
      handleKPICreateSubmit: handleKPICreateSubmit(level, parent)
    }
  }

  const rememberActiveCreateForm = (node, parentId) => {
    const formNode = { ...node, parentId }
    activeCreateFormRef.current = { node: formNode, parentId }
    return formNode
  }

  const getActiveCreateFormNode = () => activeCreateFormRef.current

  // Only one create-form at a time: opening a new one drops the previous form (with its typed text).
  const removeActiveCreateForm = (treeData) => {
    const activeForm = getFlatDataFromTree({ treeData, getNodeKey: getNodeKey as any, ignoreCollapsed: false }).find(
      ({ node }) => node.id === activeCreateFormRef.current?.node.id
    )

    return activeForm ? removeNodeAtPath({ treeData, path: activeForm.path, getNodeKey: getNodeKey as any }) : treeData
  }

  const addCreateObjectiveForm = (level, parent) => (treeData, nextLevelDescription, typeOption) => {
    const nextLevel = {
      ...level,
      description: typeOption?.label || nextLevelDescription,
      typeOptionId: typeOption?.id || null
    }
    const parentId = parent?.id || null

    const { treeData: treeWithNewNode } = addNodeUnderParent({
      treeData: removeActiveCreateForm(treeData),
      expandParent: true,
      parentKey: parentId,
      ignoreCollapsed: false,
      getNodeKey: getNodeKey as any,
      newNode: rememberActiveCreateForm(generateCreateObjectiveFormNode(nextLevel, parent), parentId)
    })

    return treeWithNewNode
  }

  const addCreateActionForm = (level, parent) => (treeData) => {
    const parentId = parent?.id || null

    const { treeData: treeWithNewNode } = addNodeUnderParent({
      treeData: removeActiveCreateForm(treeData),
      expandParent: true,
      parentKey: parentId,
      ignoreCollapsed: false,
      getNodeKey: getNodeKey as any,
      newNode: rememberActiveCreateForm(generateCreateActionFormNode(level, parent), parentId)
    })

    return treeWithNewNode
  }

  const addCreateKPIForm = (level, parent) => (treeData) => {
    const parentId = parent?.id || null

    const { treeData: treeWithNewNode } = addNodeUnderParent({
      treeData: removeActiveCreateForm(treeData),
      expandParent: true,
      parentKey: parentId,
      ignoreCollapsed: false,
      getNodeKey: getNodeKey as any,
      newNode: rememberActiveCreateForm(generateCreateKPIFormNode(level, parent), parentId)
    })

    return treeWithNewNode
  }

  const createObjective = async ({ name, lastPosition, parent, typeOptionId = null }) => {
    const parentId = parent?.id || null

    const entity = await createPlanItem({
      entityType: ENTITIES.PLAN_OBJECTIVE,
      name,
      planId: plan.id,
      parentId,
      lastPosition,
      typeOptionId
    })

    return {
      ...entity,
      entity: entity.planObjective,
      entityId: entity.planObjective.id,
      entityType: ENTITIES.PLAN_OBJECTIVE
    }
  }

  const createAction = async ({ name, lastPosition, parent }) => {
    const parentId = parent?.id || null

    const entity = await createPlanItem({
      entityType: ENTITIES.PLAN_ACTION,
      name,
      planId: plan.id,
      parentId,
      lastPosition
    })

    return { ...entity, entity: entity.planAction, entityId: entity.planAction.id, entityType: ENTITIES.PLAN_ACTION }
  }

  const createKPI = async ({ name, lastPosition, parent }) => {
    const parentId = parent?.id || null

    const entity = await createPlanItem({
      entityType: ENTITIES.PLAN_KPI,
      name,
      planId: plan.id,
      parentId,
      lastPosition
    })

    return { ...entity, entity: entity.planKPI, entityId: entity.planKPI.id, entityType: ENTITIES.PLAN_KPI }
  }

  // ---- TableTree inline create-forms (simplified: create + refetch, no manual RST tree surgery) ----
  const closeTableCreateForm = () => setTableCreateForm(null)

  const generateTableCreateFormId = (type: string) => {
    tableCreateFormCounterRef.current += 1

    return `create-${type}-${new Date().getTime()}-${tableCreateFormCounterRef.current}`
  }

  const getTableCreateHandlers = (type: string) =>
    ({
      [ENTITIES.PLAN_OBJECTIVE]: { createEntity: createObjective, linkEntity: handleLinkObjective },
      [ENTITIES.PLAN_ACTION]: { createEntity: createAction, linkEntity: handleLinkAction },
      [ENTITIES.PLAN_KPI]: { createEntity: createKPI, linkEntity: handleLinkKPI }
    })[type]

  // Mirrors the legacy tree: an item created in linking mode, or under an item referenced from the
  // parent plan, gets linked to that source right away.
  const linkCreatedTableItem = async (linkEntity, createdPlanItem, parent) => {
    if (isLinkingMode || parent?.referenced) {
      await linkEntity({ ...createdPlanItem, parent })()
    }
  }

  const submitTableCreate =
    (type: string, parent: any, typeOptionId: string | null) =>
    async ({ name }: { name: string }) => {
      const lastPosition = getUpdatedLastPosition()

      const { createEntity, linkEntity } = getTableCreateHandlers(type)

      try {
        const createdPlanItem = await createEntity({ name, lastPosition, parent, typeOptionId })

        await linkCreatedTableItem(linkEntity, createdPlanItem, parent)

        // Keep the form open so several items can be added in a row — a new id remounts it empty.
        // `prev` stays null when the user closed the form while the mutation was in flight.
        setTableCreateForm((prev) => (prev ? { ...prev, id: generateTableCreateFormId(type) } : prev))
      } catch (error) {
        handleError(error, 'ObjectivesListTreeBL.submitTableCreate')
      }
    }

  const openTableCreateForm = (type: string, parent: any, typeOption?: any) => {
    setTableCreateForm({
      type,
      parent,
      parentId: parent?.id ?? null,
      typeOptionId: typeOption?.id || null,
      id: generateTableCreateFormId(type),
      level: { ...(parent?.nextLevel || rootLevel), typeOptionId: typeOption?.id || null }
    })
  }

  const getTableCreateFormNode = () => {
    if (!tableCreateForm) {
      return null
    }

    const { id, type, parent, parentId, typeOptionId, level } = tableCreateForm

    const base = {
      id,
      parentId,
      newEntityLevel: level,
      handleTableClose: closeTableCreateForm,
      handleTableSubmit: submitTableCreate(type, parent, typeOptionId)
    }

    if (type === ENTITIES.PLAN_OBJECTIVE) {
      return { ...base, isCreateObjective: true }
    }
    if (type === ENTITIES.PLAN_ACTION) {
      return { ...base, isCreateAction: true }
    }
    if (type === ENTITIES.PLAN_KPI) {
      return { ...base, isCreateKPI: true }
    }

    return null
  }

  const handleTableRootCreateSubmit =
    () =>
    async ({ name, rootType }: { name: string; rootType: any }) => {
      const lastPosition = getUpdatedLastPosition()
      const typeOptionId = rootType.typeOption?.id || null

      const { createEntity, linkEntity } = getTableCreateHandlers(rootType.value)

      try {
        const createdPlanItem = await createEntity({ name, lastPosition, parent: null, typeOptionId })
        setFocuserState(false)

        await linkCreatedTableItem(linkEntity, createdPlanItem, null)

        // Returned so the caller can tell a successful create from a failed one (the form is kept
        // untouched on failure instead of being reset).
        return createdPlanItem
      } catch (error) {
        handleError(error, 'ObjectivesListTreeBL.handleTableRootCreateSubmit')

        return null
      }
    }

  const synchronizeTreePositions = (tree: any, filterFn: any) => {
    const formattedTree = pipe(
      formatTree,
      (data: any) => {
        if (filterFn) {
          return data.filter(filterFn)
        }

        return data
      },
      filter(({ id }) => typeof id === 'string')
    )(tree)

    const planItems = formattedTree.map(({ id, treePosition, expanded, parentId, referencedParentId }: any, index) => ({
      id,
      expanded,
      treePosition: index,
      parentId: parentId || referencedParentId
    }))

    updatePlanTree(planItems)
  }

  const debouncedSynchronizeTree: TDebouncedSynchronizeTree = debounce(synchronizeTreePositions, 1000)

  // TODO: add generateTreeDataWithLinks func
  const handleRootEntityCreateSubmit =
    (level) =>
    ({ path, tree, setTree }) =>
    async ({ name, rootType }) => {
      const lastPosition = getUpdatedLastPosition()
      const typeOptionId = rootType.typeOption?.id || null

      let createRootEntity, createLinkingItem

      if (rootType.value === ENTITIES.PLAN_OBJECTIVE) {
        createRootEntity = createObjective
        createLinkingItem = handleLinkObjective
      }

      if (rootType.value === ENTITIES.PLAN_ACTION) {
        createRootEntity = createAction
        createLinkingItem = handleLinkAction
      }

      if (rootType.value === ENTITIES.PLAN_KPI) {
        createRootEntity = createKPI
        createLinkingItem = handleLinkKPI
      }

      try {
        const entity = await createRootEntity({ name, level, lastPosition, typeOptionId })

        setDeepLevel([entity])

        if (isLinkingMode) {
          createLinkingItem(entity)()
        }

        let treeData = tree

        const { treeData: treeWithNewNode } = addNodeUnderParent({
          treeData,
          expandParent: true,
          ignoreCollapsed: false,
          getNodeKey: getNodeKey as any,
          newNode: generateNewNode({ ...entity })
        })

        treeData = treeWithNewNode

        treeData = increaseCreateFormPosition(treeData)

        treeData = removeNodeAtPath({ treeData, path, getNodeKey: getNodeKey as any })

        const { treeData: treeWithAddForm } = addNodeUnderParent({
          treeData,
          getNodeKey: getNodeKey as any,
          expandParent: true,
          ignoreCollapsed: false,
          newNode: generateCreateRootEntityNode(lastPosition, rootType)
        })

        treeData = treeWithAddForm

        setTree(treeData)
        setFocuserState(false)
      } catch (error) {
        handleError(error, 'ObjectivesList.handleRootEntityCreateSubmit')
      }
    }

  const generateTreeDataWithLinks = (data, destinationLinkingItem) => {
    return pipe(
      (treeData) =>
        getFlatDataFromTree({
          treeData: treeData,
          getNodeKey: getNodeKey as any,
          ignoreCollapsed: false
        }),
      map(({ node }) => node),
      map((item: any) => {
        const key = pipe(
          (item) => getEntityTypeDescription(item.entityType),
          (type) => `source${upperFirst(type)}Id`
        )(item)

        if (item.entityId && item.entityId === destinationLinkingItem[key]) {
          return {
            ...item,
            linkingItems: {
              ...item.linkingItems,
              sourceLinkingItems: [...item.linkingItems.sourceLinkingItems, destinationLinkingItem]
            }
          }
        }
        return item
      }),
      (flatData) =>
        getTreeFromFlatData({
          flatData: flatData,
          getKey,
          getParentKey,
          rootKey: null
        }),
      (data) => {
        setDeepLevel(data)
        return data
      }
    )(data)
  }

  const handleObjectiveCreateSubmit =
    (level, parent) =>
    ({ path, tree, setTree }) =>
    async ({ name }) => {
      const lastPosition = getUpdatedLastPosition()
      const parentId = parent?.id || null
      const parentLevel = get(parent, 'level.position') + 1

      try {
        let createdPlanItem = await createObjective({
          name,
          lastPosition,
          parent,
          typeOptionId: level.typeOptionId || null
        })

        const parentEntityPermissions = get(parent, 'entity.entityPermissions', [])
        const parentParentPermissions = get(parent, 'entity.parentPermissions', [])

        setDeepLevel([createdPlanItem], parentLevel)

        createdPlanItem = setParentPermissions(
          [createdPlanItem],
          [...parentEntityPermissions, ...parentParentPermissions]
        )?.[0]

        let destinationLinkingItem = {}

        if (isLinkingMode || parent.referenced) {
          destinationLinkingItem = await handleLinkObjective({ ...createdPlanItem, parent })()
        }

        let treeData = tree

        const { treeData: treeWithNewNode } = addNodeUnderParent({
          treeData,
          expandParent: true,
          parentKey: parentId,
          ignoreCollapsed: false,
          getNodeKey: getNodeKey as any,
          newNode: generateNewNode({
            ...omit(createdPlanItem, ['planAction', 'planKPI', 'planObjective']),
            createdPlanItem,
            entity: {
              ...createdPlanItem.planObjective,
              destinationLinkingItems: isEmpty(destinationLinkingItem) ? [] : [destinationLinkingItem]
            }
          })
        })

        treeData = treeWithNewNode

        treeData = removeNodeAtPath({ treeData, path, getNodeKey: getNodeKey as any })

        if (!isEmpty(destinationLinkingItem)) {
          treeData = generateTreeDataWithLinks(treeData, destinationLinkingItem)
        }

        if (parentId) {
          const { treeData: treeWithNewNode } = addNodeUnderParent({
            treeData,
            getNodeKey: getNodeKey as any,
            expandParent: true,
            parentKey: parentId,
            ignoreCollapsed: false,
            newNode: rememberActiveCreateForm(generateCreateObjectiveFormNode(level, parent), parentId)
          })

          treeData = treeWithNewNode
        }

        debouncedSynchronizeTree(treeData)

        setTree(treeData)
      } catch (error) {
        handleError(error, 'ObjectivesList.handleObjectiveCreateSubmit')
      }
    }

  const handleActionCreateSubmit =
    (level, parent) =>
    ({ path, tree, setTree }) =>
    async ({ name }) => {
      const lastPosition = getUpdatedLastPosition()
      const parentId = parent?.id || null
      const parentLevel = get(parent, 'level.position') + 1

      try {
        let createdPlanItem = await createAction({ name, lastPosition, parent })
        const parentEntityPermissions = get(parent, 'entity.entityPermissions', [])
        const parentParentPermissions = get(parent, 'entity.parentPermissions', [])

        setDeepLevel([createdPlanItem], parentLevel)

        createdPlanItem = setParentPermissions(
          [createdPlanItem],
          [...parentEntityPermissions, ...parentParentPermissions]
        )?.[0]

        let destinationLinkingItem = {}

        if (isLinkingMode || parent.referenced) {
          destinationLinkingItem = await handleLinkAction({ ...createdPlanItem, parent })()
        }

        let treeData = tree

        const { treeData: treeWithNewNode } = addNodeUnderParent({
          treeData,
          expandParent: true,
          parentKey: parentId,
          ignoreCollapsed: false,
          getNodeKey: getNodeKey as any,
          newNode: generateNewNode({
            ...omit(createdPlanItem, ['planAction', 'planKPI', 'planObjective']),
            entity: {
              ...createdPlanItem.planAction,
              destinationLinkingItems: isEmpty(destinationLinkingItem) ? [] : [destinationLinkingItem]
            }
          })
        })

        treeData = treeWithNewNode

        treeData = removeNodeAtPath({ treeData, path, getNodeKey: getNodeKey as any })

        if (!isEmpty(destinationLinkingItem)) {
          treeData = generateTreeDataWithLinks(treeData, destinationLinkingItem)
        }

        if (parentId) {
          const { treeData: treeWithNewNode } = addNodeUnderParent({
            treeData,
            getNodeKey: getNodeKey as any,
            expandParent: true,
            parentKey: parentId,
            ignoreCollapsed: false,
            newNode: rememberActiveCreateForm(generateCreateActionFormNode(level, parent), parentId)
          })

          treeData = treeWithNewNode
        }

        debouncedSynchronizeTree(treeData)

        setTree(treeData)
      } catch (error) {
        handleError(error, 'ObjectivesList.handleActionCreateSubmit')
      }
    }

  const handleKPICreateSubmit =
    (level, parent) =>
    ({ path, tree, setTree }) =>
    async ({ name }) => {
      const lastPosition = getUpdatedLastPosition()
      const parentId = parent?.id || null
      const parentLevel = get(parent, 'level.position') + 1

      try {
        let createdPlanItem = await createKPI({ name, lastPosition, parent })
        const parentEntityPermissions = get(parent, 'entity.entityPermissions', [])
        const parentParentPermissions = get(parent, 'entity.parentPermissions', [])

        setDeepLevel([createdPlanItem], parentLevel)

        createdPlanItem = setParentPermissions(
          [createdPlanItem],
          [...parentEntityPermissions, ...parentParentPermissions]
        )?.[0]

        let destinationLinkingItem = {}

        if (isLinkingMode || parent.referenced) {
          destinationLinkingItem = await handleLinkKPI({ ...createdPlanItem, parent })()
        }

        let treeData = tree

        const { treeData: treeWithNewNode } = addNodeUnderParent({
          treeData,
          expandParent: true,
          parentKey: parentId,
          ignoreCollapsed: false,
          getNodeKey: getNodeKey as any,
          newNode: generateNewNode({
            ...omit(createdPlanItem, ['planAction', 'planKPI', 'planObjective']),
            entity: {
              ...createdPlanItem.planKPI,
              destinationLinkingItems: isEmpty(destinationLinkingItem) ? [] : [destinationLinkingItem]
            }
          })
        })

        treeData = treeWithNewNode

        treeData = removeNodeAtPath({ treeData, path, getNodeKey: getNodeKey as any })

        if (!isEmpty(destinationLinkingItem)) {
          treeData = generateTreeDataWithLinks(treeData, destinationLinkingItem)
        }

        if (parentId) {
          const { treeData: treeWithNewNode } = addNodeUnderParent({
            treeData,
            getNodeKey: getNodeKey as any,
            expandParent: true,
            parentKey: parentId,
            ignoreCollapsed: false,
            newNode: rememberActiveCreateForm(generateCreateKPIFormNode(level, parent), parentId)
          })

          treeData = treeWithNewNode
        }

        debouncedSynchronizeTree(treeData)

        setTree(treeData)
      } catch (error) {
        handleError(error, 'ObjectivesList.handleKPICreateSubmit')
      }
    }

  const setDeepLevel = (array, deepLevel = 0, color = null) =>
    applyTreeDeepLevels(array, { levels, planTypeLevelIds }, deepLevel, color)

  const scrollToNode = (node) => {
    const getNodeLastChildPosition = (children) => {
      const lastChild = pipe<any, any, any>(
        filter((child: any) => child.treePosition),
        last
      )(children)

      if (lastChild && lastChild.children) {
        return getNodeLastChildPosition(lastChild.children)
      } else {
        return get(lastChild, 'treePosition')
      }
    }

    if (!isEmpty(get(node, 'children', []))) {
      return setSearchIndex(getNodeLastChildPosition(node.children))
    }

    return setSearchIndex(node.treePosition)
  }

  const handleSetSearchIndex = (value: number) => setSearchIndex(value)

  const value = {
    debouncedSynchronizeTree,
    entities,
    focuserState,
    generateCreateRootEntityNode,
    generateNewNode,
    getActiveCreateFormNode,
    getTableCreateFormNode,
    openTableCreateForm,
    submitRootCreate: handleTableRootCreateSubmit(),
    tableCreateForm,
    handleSetFocuserState,
    plan,
    scrollToNode,
    searchIndex,
    setDeepLevel,
    setSearchIndex: handleSetSearchIndex
  }

  return <ObjectivesListTreeBLContext.Provider value={value}>{children}</ObjectivesListTreeBLContext.Provider>
}

export default ObjectivesListTreeBL
